export const OPERATION_KIND = {
	MUTATION: "mutation",
	QUERY: "query",
	SUBSCRIPTION: "subscription"
} as const;

export type OperationKind = (typeof OPERATION_KIND)[keyof typeof OPERATION_KIND];

export interface OperationDefinition {
	kind: OperationKind;
	name?: string;
}

const KEYWORD_RE = /^(query|mutation|subscription)\b\s*([A-Za-z_][A-Za-z0-9_]*)?/;

const skipBlockString = (src: string, start: number): number => {
	let i = start + 3;
	const len = src.length;
	while (i < len) {
		if (src[i] === "\\" && i + 1 < len) {
			i += 2;
			continue;
		}
		if (src[i] === '"' && src[i + 1] === '"' && src[i + 2] === '"') return i + 3;
		i++;
	}
	return len;
};

const skipString = (src: string, start: number): number => {
	let i = start + 1;
	const len = src.length;
	while (i < len) {
		const c = src[i];
		if (c === "\\" && i + 1 < len) {
			i += 2;
			continue;
		}
		if (c === '"') return i + 1;
		if (c === "\n") return i;
		i++;
	}
	return len;
};

const skipLineComment = (src: string, start: number): number => {
	let i = start;
	const len = src.length;
	while (i < len && src[i] !== "\n") i++;
	return i;
};

export const findOperations = (query: string): OperationDefinition[] => {
	const operations: OperationDefinition[] = [];
	const len = query.length;
	let depth = 0;
	let expectOperation = true;
	let i = 0;
	while (i < len) {
		const c = query[i];
		if (c === '"') {
			if (query[i + 1] === '"' && query[i + 2] === '"') i = skipBlockString(query, i);
			else i = skipString(query, i);
			continue;
		}
		if (c === "#") {
			i = skipLineComment(query, i);
			continue;
		}
		if (depth === 0 && expectOperation) {
			if (/\s/.test(c)) {
				i++;
				continue;
			}
			if (c === "{") {
				operations.push({ kind: "query" });
				depth++;
				expectOperation = false;
				i++;
				continue;
			}
			if (c === "q" || c === "m" || c === "s") {
				const m = query.slice(i).match(KEYWORD_RE);
				if (m) {
					operations.push({ kind: m[1] as OperationKind, name: m[2] });
					i += m[0].length;
					expectOperation = false;
					continue;
				}
			}
			while (i < len && query[i] !== "{" && query[i] !== '"' && query[i] !== "#") i++;
			if (i < len && query[i] === "{") {
				depth++;
				expectOperation = false;
				i++;
			}
			continue;
		}
		if (c === "{") {
			depth++;
			i++;
			continue;
		}
		if (c === "}") {
			if (depth > 0) depth--;
			i++;
			if (depth === 0) expectOperation = true;
			continue;
		}
		i++;
	}
	return operations;
};

const documentKind = (query: unknown, operationName: unknown): OperationKind | undefined => {
	if (typeof query !== "string" || query.length === 0) return undefined;
	const ops = findOperations(query);
	if (ops.length === 0) return undefined;
	if (typeof operationName === "string" && operationName.length > 0) {
		return ops.find((op) => op.name === operationName)?.kind;
	}
	if (ops.some((op) => op.kind === "mutation")) return "mutation";
	return ops.every((op) => op.kind === "query") ? "query" : undefined;
};

const documentKindOf = (doc: unknown): OperationKind | undefined =>
	doc && typeof doc === "object"
		? documentKind((doc as { query?: unknown }).query, (doc as { operationName?: unknown }).operationName)
		: undefined;

export const resolveParsedBodyKind = (parsed: unknown): OperationKind | undefined => {
	const documents = Array.isArray(parsed) ? parsed : [parsed];
	if (documents.length === 0) return undefined;
	const kinds = documents.map(documentKindOf);
	if (kinds.includes("mutation")) return "mutation";
	return kinds.every((kind) => kind === "query") ? "query" : undefined;
};

export const resolveBodyOperationKind = (bodyText: string | undefined): OperationKind | undefined => {
	if (!bodyText) return undefined;
	try {
		return resolveParsedBodyKind(JSON.parse(bodyText));
	} catch {
		return undefined;
	}
};

export const parsedBodyHasMutation = (parsed: unknown): boolean => resolveParsedBodyKind(parsed) === "mutation";

const containsMutation = (bodyText: string): boolean => resolveBodyOperationKind(bodyText) === "mutation";

export default containsMutation;
